import React, { useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import type { ImportForhandsvisning, ImportKolonne } from '@/services/api/types';

export type { ImportKolonne };

interface Props<R> {
  data: ImportForhandsvisning<R>;
  kolonner: ImportKolonne[];
  /** Valgte radnr (bare rader uten feil kan velges). */
  valgte: Set<number>;
  /** Radnr som kan velges, i filrekkefølge. */
  velgbare: number[];
  /** Radnr med feil; vises uten avkrysningsboks. */
  feilRadnr: Set<number>;
  /** Radnr med advarsel; kan fortsatt velges. */
  advarselRadnr?: Set<number>;
  onVeksleRad: (radnr: number) => void;
  onVeksleAlle: () => void;
  oppsummering?: (forhandsvisning: ImportForhandsvisning<R>, valgte: ImportForhandsvisning<R>['rader']) => React.ReactNode;
  /** Tekst som kan vises under raden («Vis tekst»); vises som tekst i <pre> med fast tegnbredde. */
  utvidRad?: (rad: ImportForhandsvisning<R>['rader'][number]) => React.ReactNode;
}

const alleStatus = (antallValgte: number, antallVelgbare: number): boolean | 'indeterminate' => {
  if (antallVelgbare > 0 && antallValgte === antallVelgbare) return true;
  return antallValgte > 0 ? 'indeterminate' : false;
};

const STATUS_TEKST = { ny: 'Ny', finnes: 'Finnes', tekst: 'Fyller tekst' } as const;

const StatusBadge = ({ feil, advarsel, status }: { feil: boolean; advarsel: boolean; status: keyof typeof STATUS_TEKST }) => {
  if (feil) return <Badge variant="destructive">Feil</Badge>;
  return (
    <div className="flex flex-wrap gap-1">
      <Badge variant={status === 'finnes' ? 'secondary' : 'default'}>{STATUS_TEKST[status]}</Badge>
      {advarsel && <Badge variant="outline">Advarsel</Badge>}
    </div>
  );
};

const celleVerdi = (rad: Record<string, unknown>, k: ImportKolonne): string => {
  const verdi = rad[k.felt];
  const tekst = verdi === null || verdi === undefined ? null : String(verdi);
  return k.formater ? k.formater(tekst) : (tekst ?? '');
};

const ImportForhandsvisningTabell = <R,>({
  data,
  kolonner,
  valgte,
  velgbare,
  feilRadnr,
  advarselRadnr,
  onVeksleRad,
  onVeksleAlle,
  oppsummering,
  utvidRad,
}: Props<R>) => {
  const [apne, setApne] = useState<Set<number>>(new Set());
  const veksleTekst = (radnr: number) =>
    setApne((forrige) => {
      const neste = new Set(forrige);
      if (!neste.delete(radnr)) neste.add(radnr);
      return neste;
    });
  const valgteRader = data.rader.filter((r) => valgte.has(r.radnr));
  return (
    <div className="min-w-0 max-w-full space-y-2">
      {oppsummering && <p className="text-sm font-medium">{oppsummering(data, valgteRader)}</p>}
      {data.rader.length > 0 && (
        <div data-testid="import-tabellramme" className="max-h-72 w-full min-w-0 max-w-full overflow-auto rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-8">
                  <Checkbox
                    aria-label="Velg alle rader"
                    checked={alleStatus(valgte.size, velgbare.length)}
                    disabled={velgbare.length === 0}
                    onCheckedChange={onVeksleAlle}
                  />
                </TableHead>
                <TableHead>Rad</TableHead>
                {kolonner.map((k) => (
                  <TableHead key={k.felt}>{k.overskrift}</TableHead>
                ))}
                <TableHead>Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.rader.map((rad) => {
                const harFeil = feilRadnr.has(rad.radnr);
                const utvidet = utvidRad?.(rad);
                const erApen = apne.has(rad.radnr);
                return (
                  <React.Fragment key={rad.radnr}>
                  <TableRow>
                    <TableCell>
                      {!harFeil && (
                        <Checkbox
                          aria-label={`Velg rad ${rad.radnr}`}
                          checked={valgte.has(rad.radnr)}
                          onCheckedChange={() => onVeksleRad(rad.radnr)}
                        />
                      )}
                    </TableCell>
                    <TableCell>{rad.radnr}</TableCell>
                    {kolonner.map((k) => (
                      <TableCell key={k.felt}>{celleVerdi(rad as Record<string, unknown>, k)}</TableCell>
                    ))}
                    <TableCell>
                      <StatusBadge feil={harFeil} advarsel={!!advarselRadnr?.has(rad.radnr)} status={rad.status} />
                      {utvidet ? (
                        <Button type="button" variant="link" size="sm" className="h-auto p-0" onClick={() => veksleTekst(rad.radnr)}>
                          {erApen ? 'Skjul tekst' : 'Vis tekst'}
                        </Button>
                      ) : null}
                    </TableCell>
                  </TableRow>
                  {utvidet && erApen ? (
                    <TableRow>
                      <TableCell colSpan={kolonner.length + 3}>
                        <pre role="region" aria-label="Sangtekst" tabIndex={0} className="max-h-64 overflow-auto whitespace-pre font-mono text-xs">{utvidet}</pre>
                      </TableCell>
                    </TableRow>
                  ) : null}
                  </React.Fragment>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
};

export default ImportForhandsvisningTabell;
